import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import * as api from 'api/api'
import { Attachment, Section } from 'api/types'
import { Icon } from 'components/Icon'
import { useProject } from 'components/Layout'
import { Button, Card, EmptyState, ErrorState, SelectField, Spinner, TextField } from 'components/ui'
import { SECTION_LABELS, SECTION_ORDER, formatDate, formatSize, isImage } from 'lib/format'
import { t } from 'lib/i18n'
import { useAction, useAsync } from 'lib/hooks'

import { PreviewModal, Thumb } from './report/Attachments'

export const AttachmentsPage = () => {
  const { project } = useProject()
  const [section, setSection] = useState<Section | ''>('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [preview, setPreview] = useState<Attachment | null>(null)
  const { busy, run } = useAction()

  const { data, error, loading, reload } = useAsync(
    () => api.fetchProjectAttachments(project.id, { from: from || undefined, section: section || undefined, to: to || undefined }),
    [project.id, section, from, to]
  )

  const groups = useMemo(() => {
    const map = new Map<string, { code: string; date: string; reportId: string; items: Attachment[] }>()
    data?.forEach((attachment) => {
      const group = map.get(attachment.reportId) ?? {
        code: attachment.reportCode ?? '',
        date: attachment.reportDate ?? '',
        items: [],
        reportId: attachment.reportId,
      }
      group.items.push(attachment)
      map.set(attachment.reportId, group)
    })
    return Array.from(map.values())
  }, [data])

  const totalSize = data?.reduce((sum, item) => sum + item.size, 0) ?? 0

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('أرشيف المرفقات', 'Attachments')}</h1>
          <p className="muted">
            {data
              ? t(`${data.length} ملف · ${formatSize(totalSize)}`, `${data.length} files · ${formatSize(totalSize)}`)
              : t('صور الموقع، نتائج الفحوصات وسندات التوريد لكل التقارير', 'Site photos, lab results, and delivery notes for every report')}
          </p>
        </div>
      </div>

      <Card>
        <div className="filters">
          <SelectField label={t('القسم', 'Section')} value={section} onChange={(event) => setSection(event.target.value as Section | '')}>
            <option value="">{t('كل الأقسام', 'All sections')}</option>
            {SECTION_ORDER.map((key) => (
              <option key={key} value={key}>
                {SECTION_LABELS[key]}
              </option>
            ))}
          </SelectField>
          <TextField label={t('من تاريخ', 'From')} type="date" min={project.startDate} max={project.endDate} value={from} onChange={(event) => setFrom(event.target.value)} />
          <TextField label={t('إلى تاريخ', 'To')} type="date" min={project.startDate} max={project.endDate} value={to} onChange={(event) => setTo(event.target.value)} />
          {section || from || to ? (
            <Button
              variant="ghost"
              icon="close"
              onClick={() => {
                setSection('')
                setFrom('')
                setTo('')
              }}
            >
              {t('مسح التصفية', 'Clear filters')}
            </Button>
          ) : null}
        </div>
      </Card>

      {loading && !data ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !groups.length ? (
        <EmptyState title={t('لا توجد مرفقات مطابقة', 'No matching attachments')} icon="paperclip" />
      ) : (
        groups.map((group) => (
          <Card
            key={group.reportId}
            title={
              <Link to={`/projects/${project.id}/reports/${group.reportId}`} className="code-link">
                {group.code} · {formatDate(group.date)}
              </Link>
            }
            actions={
              <Button
                size="sm"
                icon="folder"
                busy={busy === group.reportId}
                onClick={() => run(group.reportId, () => api.downloadReportAttachments(group.reportId))}
              >
                {t('تنزيل الكل (zip)', 'Download all (zip)')}
              </Button>
            }
          >
            <ul className="archive">
              {group.items.map((attachment) => (
                <li key={attachment.id}>
                  <button
                    type="button"
                    className="archive__item"
                    onClick={() =>
                      isImage(attachment.mimeType)
                        ? setPreview(attachment)
                        : run(attachment.id, () => api.downloadAttachment(attachment.id))
                    }
                  >
                    <span className="archive__thumb">
                      <Thumb attachment={attachment} />
                    </span>
                    <span className="archive__meta">
                      <strong>{attachment.caption || attachment.originalName}</strong>
                      <small>
                        {SECTION_LABELS[attachment.section]}
                        {attachment.lineId ? t(' · مرتبط ببند', ' · Linked to a line') : ''} · {formatSize(attachment.size)}
                      </small>
                      <small>{attachment.uploadedByName}</small>
                    </span>
                    <Icon name={isImage(attachment.mimeType) ? 'eye' : 'download'} />
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        ))
      )}

      {preview ? <PreviewModal attachment={preview} onClose={() => setPreview(null)} /> : null}
    </div>
  )
}
